import React, { useEffect } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import { subscribe } from 'react-contextual'
import { Button, Checkbox, CurrencyField, DatePicker, Divider, FormField, Select } from 'components'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce } from 'react-use'
import { format } from 'date-fns'
import { find, path } from 'ramda'
import { chatbotSelectors } from '../../../../ui/ChatBot'
import { formUtils } from '../../../../../utils'
import { portfolioApiEvents, portfolioApiSelectors } from '../../../../api/portfolio'
import PortfoliosProvider from '../../../PortfoliosProvider'
import LeaseDetails from './LeaseDetails/LeaseDetails'
import NextSteps from './NextSteps'
import TerminationWarning from './TerminationWarning/TerminationWarning'
import styles from './PortfolioTerminationForm.module.scss'
import { partyApiSelectors } from '../../../../api/party'
import { walletApiSelectors } from '../../../../api/wallet'
import { invoiceApiSelectors } from '../../../../api/invoice'
import { reconApiSelectors } from '../../../../api/recon'
import { apiStateSelectors } from '../../../../api/apiState'
import { userApiSelectors } from '../../../../api/user'
import { $TSFixMe } from 'types/ts-migrate'
import { fromCents } from 'utils/currency'

interface PortfolioTerminationFormProps {
  portfolioId: string
  openPortfolio: (portfolioId: string) => void
  fetchDraftInvoices: () => void
  fetchActiveInvoices: () => void
  openActiveInvoices: () => void
  openDraftInvoices: () => void
}

const validationSchema = yup.object().shape({
  reason: yup.object().required('Required'),
})

const PortfolioTerminationForm = ({
  openPortfolio,
  fetchDraftInvoices,
  fetchActiveInvoices,
  openActiveInvoices,
  openDraftInvoices,
}: PortfolioTerminationFormProps) => {
  const dispatch = useDispatch()
  const currentPortfolioId = useSelector(chatbotSelectors.getCurrentPortfolioId)
  const isTerminating = useSelector(state =>
    apiStateSelectors.isLoading(state)([portfolioApiEvents.terminateApprovedLease_request]),
  )
  const terminationReasons: $TSFixMe[] = useSelector(portfolioApiSelectors.getLeaseTerminationReasons)
  const isPortfolioTerminated = useSelector(state =>
    portfolioApiSelectors.isPortfolioTerminated(state)(currentPortfolioId),
  )
  const tenantId = useSelector(state => portfolioApiSelectors.getTenantByPortfolioId(state)(currentPortfolioId))
  const tenantAccounts = useSelector(state => partyApiSelectors.getPartyAccountsById(state)(tenantId))
  const portfolioAccount = find((acc: any) => acc.portfolioId === currentPortfolioId, tenantAccounts)
  const depositAccountBalance = useSelector(state =>
    walletApiSelectors.getDepositAccountBalance(state)(tenantId, path(['accountId'], portfolioAccount)),
  )
  const draftInvoices = useSelector(state =>
    invoiceApiSelectors.getDraftInvoicesForPortfolio(state)(currentPortfolioId),
  )
  const activeInvoices = useSelector(state =>
    reconApiSelectors.getActiveInvoicesForPortfolio(state)(currentPortfolioId),
  )
  const isReadOnly = useSelector(userApiSelectors.isReadOnlyRole)
  const isOwnerRole = useSelector(userApiSelectors.isOwnerRole)

  const initialValues = {
    vacateDate: new Date(),
    reason: '',
    understandAction: false,
    releaseDeposit: 'false' as 'true' | 'false', // string value in form due to the way radios work, cast to bool on submission
    cancellationFee: 0,
    cancellationFeeVatable: false,
  }

  useEffectOnce(() => {
    dispatch(portfolioApiEvents.terminationReasons_request())
    fetchDraftInvoices()
    fetchActiveInvoices()
  })

  useEffect(() => {
    if (!isTerminating && isPortfolioTerminated) {
      openPortfolio(currentPortfolioId)
    }
  }, [isTerminating, isPortfolioTerminated, currentPortfolioId, openPortfolio])

  const handleSubmit = (values: any, { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }) => {
    const payload = {
      body: {
        vacateDate: format(values.vacateDate, 'yyyy-MM-dd'),
        reason: values.reason.value,
        understandAction: values.understandAction,
        releaseDeposit: values.releaseDeposit === 'true',
        cancellationFee: {
          netAmount: fromCents(values.cancellationFee),
          vatable: values.cancellationFeeVatable,
        },
      },
      params: { id: currentPortfolioId },
    }
    dispatch(portfolioApiEvents.terminateApprovedLease_request(payload))
    setSubmitting(false)
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, handleSubmit, errors, touched, setFieldValue, setFieldTouched, submitCount }) => {
        const getFieldError = formUtils.getFieldError(submitCount, touched, errors)

        return (
          <form onSubmit={handleSubmit} className={styles.root}>
            <fieldset disabled={isReadOnly}>
              <div className={styles['required-fields-info']}>
                Fields marked with <span>*</span> are required.
              </div>

              <LeaseDetails portfolioId={currentPortfolioId} depositHeld={depositAccountBalance} />

              <div className="rbn--row">
                <div className="rbn--col-md-4">
                  <FormField>
                    <DatePicker
                      label="Vacate date"
                      name="vacateDate"
                      required={true}
                      onChange={(date: any) => setFieldValue('vacateDate', date)}
                      date={values.vacateDate}
                    />
                  </FormField>
                </div>
                <div className="rbn--col-md-4">
                  <FormField>
                    <Select
                      name="reason"
                      value={terminationReasons.find((r: any) => r.value === values.reason)}
                      label="Reason for termination"
                      required={true}
                      error={getFieldError('reason')}
                      options={terminationReasons.map((r: any) => ({
                        label: r.name,
                        value: r.value,
                      }))}
                      onBlur={() => setFieldTouched('reason')}
                      onChange={(option: any) => setFieldValue('reason', option)}
                    />
                  </FormField>
                </div>
              </div>

              <div className="rbn--row">
                <div className="rbn--col-md-4">
                  <FormField>
                    <CurrencyField
                      includeVatControl
                      vatApplied={values.cancellationFeeVatable}
                      name="cancellationFee"
                      label="Cancellation fee"
                      labelHint="Generates a once-off draft cancellation fee invoice."
                      value={values.cancellationFee}
                      onChange={({ target }: any) => setFieldValue('cancellationFee', target.value)}
                      onToggleChange={(vatApplied: boolean) => setFieldValue('cancellationFeeVatable', vatApplied)}
                    />
                  </FormField>
                </div>
              </div>

              <Divider />

              <TerminationWarning
                draftInvoices={draftInvoices.length}
                activeInvoices={activeInvoices.length}
                openActiveInvoices={openActiveInvoices}
                openDraftInvoices={openDraftInvoices}
              />

              <NextSteps cancellationFee={Number(values.cancellationFee)} />

              <Checkbox
                className={styles.understand}
                label="I understand that this action cannot be undone."
                name="understandAction"
                checked={values.understandAction}
                onChange={() => setFieldValue('understandAction', !values.understandAction)}
              />

              <div className={styles.footer}>
                <Button type="submit" disabled={!values.understandAction} loading={isTerminating}>
                  Terminate lease
                </Button>
              </div>
            </fieldset>
          </form>
        )
      }}
    </Formik>
  )
}

export default subscribe(
  [PortfoliosProvider],
  ({ openPortfolio, fetchDraftInvoices, fetchActiveInvoices, openActiveInvoices, openDraftInvoices }: any) => ({
    openPortfolio,
    fetchDraftInvoices,
    fetchActiveInvoices,
    openActiveInvoices,
    openDraftInvoices,
  }),
)(PortfolioTerminationForm)
